import { faXmark } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { type CardIdentifier } from "@kanpla/api-contract";
import { useForm } from "react-hook-form";

import { T, useT } from "@repo/transifex";
import { useUpdateChildCardIdentifier } from "~/api/children/hooks/use-update-child-card-identifier";
import { Button } from "~/components/Button";
import { Dialog } from "~/components/Dialog";
import { DisplayErrorMessage } from "~/components/display-error-message/display-error-message";
import { useToast } from "~/components/hooks/use-toast";
import { BaseInput } from "~/components/Inputs/BaseInput";
import { ScannerListener } from "~/components/scanner-listener";
import { useCollectTillContext } from "~/routes/__app.collect/collect-till-context";

type EditKanplaCardIdModalProps = {
  childId: string;
  currentCardIdentifier: CardIdentifier | null;
  onClose: () => void;
};

type FormData = {
  cardIdentifier: string;
};

export const EditKanplaCardIdModal = ({
  childId,
  currentCardIdentifier,
  onClose
}: EditKanplaCardIdModalProps) => {
  const t = useT();
  const { toast } = useToast();
  const tillId = useCollectTillContext((till) => till.id);

  const { register, handleSubmit, setValue, watch, reset } = useForm<FormData>({
    defaultValues: {
      cardIdentifier: currentCardIdentifier?.value?.toString() ?? ""
    }
  });

  const cardIdentifier = watch("cardIdentifier");

  const {
    mutate: updateChildCardIdentifier,
    status,
    error,
    reset: resetMutation
  } = useUpdateChildCardIdentifier();

  const handleIdentifyProductOrUser: React.ComponentProps<
    typeof ScannerListener
  >["onIdentifyProductOrUser"] = (result) => {
    setValue("cardIdentifier", result);
  };

  const handleIdentifyUser: React.ComponentProps<
    typeof ScannerListener
  >["onIdentifyUser"] = (result) => {
    setValue("cardIdentifier", result.value);
  };

  const onSubmit = (data: FormData) => {
    updateChildCardIdentifier(
      {
        tillId,
        childId,
        cardIdentifier:
          currentCardIdentifier?.type === "kanpla-id"
            ? {
                type: "kanpla-id",
                value: Number.parseInt(data.cardIdentifier, 10)
              }
            : {
                type: "employee-card-hex",
                value: data.cardIdentifier
              }
      },
      {
        onSuccess: () => {
          toast({
            title: t("Card Identifier updated"),
            variant: "success"
          });
          reset();
          resetMutation();
          onClose();
        }
      }
    );
  };

  const hasChanged =
    cardIdentifier !== (currentCardIdentifier?.value?.toString() ?? "");

  return (
    <Dialog size="md" onOutsideClick={onClose} open>
      <ScannerListener
        isUserAuthenticated={false}
        onIdentifyUser={handleIdentifyUser}
        onIdentifyProductOrUser={handleIdentifyProductOrUser}
        enabled={status !== "pending"}
      />
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="relative flex w-full flex-col items-center gap-8 px-10 py-5"
      >
        {/* Close button */}
        <button
          type="button"
          className="absolute right-4 top-4 text-text-secondary hover:text-text-primary"
          onClick={onClose}
          aria-label={t("Close")}
        >
          <FontAwesomeIcon icon={faXmark} className="size-5" />
        </button>

        <div className="mt-4 flex w-full flex-col items-center gap-4">
          <h1 className="text-2xl font-bold">
            <T _str="Edit Card Identifier" />
          </h1>

          <div className="w-full space-y-4">
            <BaseInput
              className="w-full text-center text-lg"
              type={
                currentCardIdentifier?.type === "kanpla-id" ? "number" : "text"
              }
              {...register("cardIdentifier")}
              placeholder={t("Enter Card Identifier")}
              disabled={status === "pending"}
            />

            {error ? (
              <div className="rounded-md border border-red-500 p-2 text-red-500">
                <DisplayErrorMessage error={error} />
              </div>
            ) : null}
          </div>

          <div className="flex w-full gap-3">
            <Button
              type="button"
              className="flex-1"
              variant="danger-light"
              onClick={onClose}
              disabled={status === "pending"}
            >
              <T _str="Cancel" />
            </Button>
            <Button
              type="submit"
              className="flex-1"
              variant="primary"
              disabled={!hasChanged || status === "pending"}
              loading={status === "pending"}
            >
              <T _str="Confirm" />
            </Button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};
